import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ModalOptions, BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";

import { EditVehicleModalComponent } from "../../../../dialogs/edit-vehicle-modal/edit-vehicle-modal.component";
import { VehicleModel } from "../../../../models/vehicle-model";
import { VehicleType } from "../../../../models/vehicle-type";
import { VehiclesHandlerService } from "../../../../api-handlers/vehicles-handler.service";

@Component({
  selector: "app-manage-vehicles",
  templateUrl: "./manage-vehicles.component.html",
  styleUrls: ["./manage-vehicles.component.css"],
})
export class ManageVehiclesComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  vehiclesTableDataSource: MatTableDataSource<Object>;
  vehicleDisplayedColumns: string[] = [
    "id",
    "plate",
    "closed_at",
    "modelName",
    "modelBrandName",
    "typeSupertypeName",
    "typeName",
    "km",
    "actions",
  ];

  form: FormGroup = this.formBuilder.group({
    targa: [
      "",
      [Validators.required, Validators.minLength(6), Validators.maxLength(8)],
    ],
    modello: [null, [Validators.required]],
    tipo: [null, [Validators.required]],
    sottoclasseText: ["", []],
    classeText: ["", []],
    marcaText: ["", []],
    modelloText: ["", []],
  });

  addVehicleModalRef?: BsModalRef;
  editVehicleModalRef?: BsModalRef;

  errorMessage = "";
  loading = false;
  submitted = false;

  models: VehicleModel[] = [];
  classes: VehicleType[] = [];

  parkId: number;
  parkName: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private modalService: BsModalService,
    private snackBar: MatSnackBar,
    private vehicleService: VehiclesHandlerService
  ) {}

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe((params) => {
      this.parkId = Number(params.get("parkId"));
      this.parkName = decodeURI(params.get("parkName"));

      (async () => {
        try {
          const getVehiclesResponse = await this.vehicleService.getAllByParkID(
            params.get("parkId")
          );

          getVehiclesResponse.forEach((vehicle) => {
            vehicle.modelName = vehicle.model.name;
            vehicle.modelBrandName = vehicle.model.brand.name;
            vehicle.typeSupertypeName = vehicle.type.supertype.name;
            vehicle.typeName = vehicle.type.name;
          });

          this.vehiclesTableDataSource = new MatTableDataSource(
            getVehiclesResponse
          );
          this.vehiclesTableDataSource.paginator = this.paginator;
          this.vehiclesTableDataSource.sort = this.sort;

          this.vehiclesTableDataSource.filterPredicate = (
            data: any,
            filter: string
          ): boolean =>
            data.plate ? data.plate.toLowerCase().includes(filter) : false;

          this.resetForm();
        } catch (error) {
          this.errorMessage = error.message;
        }
      })();
    });
  }

  openAddVehicleModal(template: TemplateRef<any>) {
    this.addVehicleModalRef = this.modalService.show(template, {
      ignoreBackdropClick: true,
      keyboard: false,
    });

    this.addVehicleModalRef.onHide.subscribe(this.getUpdatedVehiclesData);
  }

  applyVehicleSearchFilter(filterVal) {
    this.vehiclesTableDataSource.filter = filterVal.trim().toLowerCase();
  }

  onVehicleRowActionHandler(rowData) {
    const initialState: ModalOptions = {
      initialState: {
        vehicleId: rowData.id,
        parkId: this.parkId,
        plate: rowData.plate,
        closed_at: rowData.closed_at ? "1" : "0",
      },
      ignoreBackdropClick: true,
      keyboard: false,
    };

    this.editVehicleModalRef = this.modalService.show(
      EditVehicleModalComponent,
      initialState
    );

    this.editVehicleModalRef.onHide.subscribe(this.getUpdatedVehiclesData);
  }

  resetForm() {
    this.form.reset();
    this.submitted = false;
    this.loading = false;
    this.models = [];
    this.classes = [];
    this.searchClassi();
    this.searchModelli();
  }

  get cp() {
    return this.form.controls;
  }

  async onSubmit() {
    this.submitted = true;
    this.errorMessage = "";

    if (this.form.invalid) {
      return;
    }

    this.loading = true;
    let plate = this.cp.targa.value.toUpperCase().trim();
    if (!plate.startsWith("CRI")) {
      plate = "CRI" + plate;
    }

    try {
      await this.vehicleService.create(
        plate,
        this.cp.modello.value,
        this.cp.tipo.value,
        this.parkId
      );

      this.resetForm();
      this.snackBar.open("Veicolo creato", undefined, { duration: 3000 });
      this.addVehicleModalRef.hide();
    } catch (error) {
      this.errorMessage = "Impossibile creare il veicolo";
      this.loading = false;
    }
  }

  getUpdatedVehiclesData = async () => {
    try {
      const getVehiclesResponse = await this.vehicleService.getAllByParkID(
        this.parkId + ""
      );

      getVehiclesResponse.forEach((vehicle) => {
        vehicle.modelName = vehicle.model.name;
        vehicle.modelBrandName = vehicle.model.brand.name;
        vehicle.typeSupertypeName = vehicle.type.supertype.name;
        vehicle.typeName = vehicle.type.name;
      });

      this.vehiclesTableDataSource = new MatTableDataSource(
        getVehiclesResponse
      );
      this.vehiclesTableDataSource.paginator = this.paginator;
      this.vehiclesTableDataSource.sort = this.sort;

      this.vehiclesTableDataSource.filterPredicate = (
        data: any,
        filter: string
      ): boolean => {
        return data.plate.toLowerCase().includes(filter);
      };
    } catch (error) {
      this.errorMessage = error.message;
    }
  };

  onClasseChanged(sottoclasse: string) {
    this.searchClassi();
  }

  onSottoclasseChanged(classe: string) {
    this.searchClassi();
  }

  onModelloChanged(modello: string) {
    this.searchModelli();
  }

  onMarcaChanged(marca: string) {
    this.searchModelli();
  }

  async searchModelli() {
    try {
      const searchModelsRes = await this.vehicleService.getModels(
        this.cp.modelloText.value,
        this.cp.marcaText.value
      );

      this.models = searchModelsRes;
    } catch (error) {
      this.errorMessage = error.message;
    }
  }

  async searchClassi() {
    try {
      const res = await this.vehicleService.getClasses(
        this.cp.sottoclasseText.value,
        this.cp.classeText.value
      );

      this.classes = res;
    } catch (error) {
      this.errorMessage = error.message;
    }
  }

  modelloSelected(modello: VehicleModel) {
    this.cp.modello.setValue(modello.id);
    this.form.updateValueAndValidity();
  }

  classeSelected(classe: VehicleType) {
    this.cp.tipo.setValue(classe.id);
    this.form.updateValueAndValidity();
  }
}
